/**
 * Paladin kit identity (plan 29 F9, lane BX 2026-10-11).
 *
 * The paladin auto, Crusader Strike and Divine Storm all showed the same
 * yellow disc and star (the generated holy archetype burst); Hammer of
 * Wrath's hammer never read; Flash of Light wore Flash Mend's quick_heal;
 * Divine Shield had only a faint flash; Shield of the Righteous borrowed
 * Divine Shield's cast. Each def here is named by the ability's TOML
 * (`cast_effect` / `impact_effect`) so the local module, the confirmed
 * impact and a remote replay all read the same id.
 *
 * Palette (TOML `aura_color`): auto burnished bronze-gold, Divine Storm
 * sun-orange, Hammer of Wrath radiant amber, Flash of Light white,
 * Divine Shield pale holy blue, Shield of the Righteous steel blue on gold,
 * Consecration gold. One-shot bursts end; the two states are persistent
 * `status` defs mapped in STATUS_PRESENTATIONS. No screen shake or flash.
 */
import type { VFXEffectConfig } from '../../VFXEffectRegistry';
import { ringBlast, slashArc, shardBurst, glintScatter, groundCloud, risingColumn, wispRise } from './burstBuilders';

const BRONZE = 0xd9a033;
const BRONZE_DARK = 0x8a6a3a;
const SUN = 0xff7f2a;
const SUN_CORE = 0xffe2b0;
const AMBER = 0xffb347;
const WHITE = 0xffffff;
const SHIELD_BLUE = 0xcfe8ff;
const SHIELD_GOLD = 0xffe9a0;
const STEEL_BLUE = 0x6fa8ff;
const GOLD = 0xffd700;

/** Paladin auto: a short bronze clang — metal on bone, no star, no disc. */
const HAMMER_TAP_IMPACT: VFXEffectConfig = {
  id: 'hammer_tap_impact',
  name: 'Hammer Tap',
  category: 'burst',
  description: 'A short bronze clang ring with four dents knocked outward — a hammer blow, not a spell',
  primitives: [
    ringBlast({ radius: 14, color: BRONZE, alpha: 0.75, stroke: { color: BRONZE_DARK, width: 3 }, expandTo: 1.9, durationMs: 220 }),
    shardBurst({ count: 4, spread: 18, color: BRONZE, shard: { width: 9, height: 4 }, travel: 34, durationMs: 220 }),
    glintScatter({ count: 3, spread: 14, color: 0xfff2d0, durationMs: 160, delayMs: 30 }),
  ],
};

/** Divine Storm's zone: a sun-orange vortex of light blades, no centre disc. */
const DIVINE_STORM_VORTEX: VFXEffectConfig = {
  id: 'divine_storm_vortex',
  name: 'Divine Storm Vortex',
  category: 'burst',
  description: 'Three sun-orange light blades wheel around the paladin inside a thin ring, motes rising',
  primitives: [
    { type: 'circle', radius: 58, color: SUN, alpha: 0.08, depth: 92,
      stroke: { color: SUN, width: 2 },
      tween: { alpha: 0, scale: 1.12, duration: 520, ease: 'Sine.easeOut' } },
    slashArc({ radius: 46, startDeg: -60, endDeg: 30, width: 9, color: SUN_CORE, durationMs: 300, glow: true }),
    slashArc({ radius: 50, startDeg: 60, endDeg: 150, width: 8, color: SUN, durationMs: 320, fadeDelayMs: 80 }),
    slashArc({ radius: 54, startDeg: 180, endDeg: 270, width: 8, color: SUN, durationMs: 340, fadeDelayMs: 160 }),
    wispRise({ count: 8, spread: 50, radius: 3, color: SUN_CORE, travel: 30, durationMs: 560, glow: true }),
  ],
};

/** Divine Storm's hit: a sun-orange X of light. */
const DIVINE_STORM_STRIKE: VFXEffectConfig = {
  id: 'divine_storm_strike',
  name: 'Divine Storm Strike',
  category: 'burst',
  description: 'Two crossing sun-orange light cuts with a hot core — the storm passing through',
  primitives: [
    slashArc({ radius: 22, startDeg: -80, endDeg: 0, width: 6, color: SUN_CORE, durationMs: 200, glow: true }),
    slashArc({ radius: 22, startDeg: 100, endDeg: 180, width: 6, color: SUN, durationMs: 220, fadeDelayMs: 40 }),
    { type: 'circle', radius: 6, color: SUN_CORE, alpha: 0.95, depth: 97, glow: true,
      tween: { alpha: 0, scale: 1.8, duration: 180, ease: 'Power1', delay: 40 } },
  ],
};

/** Hammer of Wrath's hand flash: the hammer of light is raised. */
const WRATH_HAMMER_RAISE: VFXEffectConfig = {
  id: 'wrath_hammer_raise',
  name: 'Wrath Hammer Raise',
  category: 'burst',
  description: 'A hammer of amber light flashes above the paladin as it is hurled',
  primitives: [
    { type: 'rect', width: 4, height: 22, offsetY: -30, color: BRONZE_DARK, alpha: 0.9, depth: 101,
      tween: { y: -8, alpha: 0, duration: 260, ease: 'Sine.easeOut' } },
    { type: 'rect', width: 22, height: 12, offsetY: -44, color: AMBER, alpha: 0.95, depth: 102, glow: true,
      stroke: { color: WHITE, width: 2 },
      tween: { y: -8, alpha: 0, duration: 260, ease: 'Sine.easeOut' } },
    glintScatter({ count: 4, spread: 18, color: WHITE, durationMs: 220 }),
  ],
};

/** Hammer of Wrath's hit: the hammer slams down in a cross of light. */
const WRATH_HAMMER_IMPACT: VFXEffectConfig = {
  id: 'wrath_hammer_impact',
  name: 'Wrath Hammer Impact',
  category: 'burst',
  description: 'An amber hammer silhouette slams onto the target, four rays of light and a ground ring',
  primitives: [
    { type: 'rect', width: 26, height: 14, offsetY: -28, color: AMBER, alpha: 0.95, depth: 98, glow: true,
      stroke: { color: WHITE, width: 2 },
      tween: { y: 22, alpha: 0, duration: 240, ease: 'Power3.easeIn' } },
    { type: 'star', points: 4, innerRadius: 3, outerRadius: 34, color: WHITE, alpha: 0.9, depth: 97, glow: true,
      tween: { alpha: 0, scale: 1.3, duration: 260, ease: 'Power2', delay: 120 } },
    ringBlast({ radius: 18, color: AMBER, alpha: 0.6, stroke: { color: WHITE, width: 2 }, expandTo: 2.2, durationMs: 320, delayMs: 140 }),
    groundCloud({ width: 56, height: 14, color: GOLD, alpha: 0.35, offsetY: 18, durationMs: 420 }),
  ],
};

/** Flash of Light: a white lance of light down onto the healed and a ring. */
const FLASH_OF_LIGHT_BURST: VFXEffectConfig = {
  id: 'flash_of_light_burst',
  name: 'Flash of Light',
  category: 'burst',
  description: 'A narrow white lance of light falls onto the healed player, a ring at the feet and rising sparks',
  primitives: [
    risingColumn({ width: 10, height: 90, color: WHITE, alpha: 0.85, glow: true, riseBy: -10, durationMs: 300 }),
    ringBlast({ radius: 20, color: GOLD, alpha: 0.35, stroke: { color: WHITE, width: 2 }, expandTo: 1.8, durationMs: 340 }),
    wispRise({ count: 5, spread: 18, radius: 2.5, color: WHITE, travel: 36, durationMs: 520, glow: true }),
  ],
};

/** Divine Shield's cast: a pale blue bubble seals around the paladin. */
const DIVINE_SHIELD_CAST: VFXEffectConfig = {
  id: 'divine_shield_cast',
  name: 'Divine Shield Seal',
  category: 'burst',
  description: 'A pale holy-blue ring closes in on the paladin and seals into a bubble',
  primitives: [
    { type: 'circle', radius: 70, color: SHIELD_BLUE, alpha: 0.05, depth: 99,
      stroke: { color: SHIELD_BLUE, width: 3 },
      tween: { scale: 0.75, alpha: 0.5, duration: 220, ease: 'Sine.easeIn' } },
    glintScatter({ count: 6, spread: 50, color: SHIELD_GOLD, durationMs: 260, delayMs: 160 }),
  ],
};

/** Divine Shield's state: a pale holy-blue bubble with a turning gold seam. */
const DIVINE_SHIELD_STATE: VFXEffectConfig = {
  id: 'divine_shield_state',
  name: 'Divine Shield State',
  category: 'status',
  description: 'A pale holy-blue bubble around the body with a gold seam turning on it: immune while it stands',
  followTarget: 'player',
  primitives: [
    { type: 'ellipse', width: 96, height: 104, color: SHIELD_BLUE, alpha: 0.2, depth: 99,
      stroke: { color: SHIELD_BLUE, width: 3 },
      tween: { alpha: 0.32, scale: 1.03, duration: 650, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' } },
    { type: 'arc', arcRadius: 50, arcStartAngle: -40, arcEndAngle: 40, color: SHIELD_GOLD, alpha: 0.95, depth: 101,
      stroke: { color: SHIELD_GOLD, width: 3 },
      tween: { rotation: Math.PI * 2, duration: 2200, repeat: -1, ease: 'Linear' } },
  ],
};

/** Shield of the Righteous: the shield's clang on the target. */
const RIGHTEOUS_SHIELD_SLAM: VFXEffectConfig = {
  id: 'righteous_shield_slam',
  name: 'Righteous Shield Slam',
  category: 'burst',
  description: 'A steel-blue kite shield flashes on the target with a gold clang ring and sparks',
  primitives: [
    { type: 'polygon', polygonPoints: [0, -16, 10, -12, 10, 2, 0, 16, -10, 2, -10, -12],
      color: STEEL_BLUE, alpha: 0.9, depth: 98, stroke: { color: SHIELD_GOLD, width: 2 },
      tween: { alpha: 0, scale: 1.35, duration: 240, ease: 'Power2' } },
    ringBlast({ radius: 16, color: GOLD, alpha: 0.6, stroke: { color: SHIELD_GOLD, width: 3 }, expandTo: 2.1, durationMs: 280 }),
    shardBurst({ count: 5, spread: 22, color: SHIELD_GOLD, shard: { width: 6, height: 2 }, durationMs: 240, glow: true }),
  ],
};

/** Shield of the Righteous's state: a small gold-rimmed shield held up. */
const RIGHTEOUS_SHIELD_STATE: VFXEffectConfig = {
  id: 'righteous_shield_state',
  name: 'Righteous Shield State',
  category: 'status',
  description: 'A steel-blue, gold-rimmed shield held at the paladin\'s side: damage taken is cut while it stays',
  followTarget: 'player',
  primitives: [
    { type: 'polygon', polygonPoints: [-9, -16, 9, -16, 10, 4, 0, 18, -10, 4], offsetX: 32, offsetY: 2,
      color: STEEL_BLUE, alpha: 0.85, depth: 101, stroke: { color: SHIELD_GOLD, width: 2 },
      tween: { alpha: 0.6, duration: 700, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' } },
    { type: 'rect', width: 3, height: 22, offsetX: 32, offsetY: 0, color: SHIELD_GOLD, alpha: 0.9, depth: 102 },
  ],
};

/** Consecration's hit: a gold flame licks up from the hallowed ground. */
const CONSECRATION_BURN: VFXEffectConfig = {
  id: 'consecration_burn',
  name: 'Consecration Burn',
  category: 'burst',
  description: 'A gold flame licks up from the ground under the target — burned by the consecrated earth',
  primitives: [
    { type: 'ellipse', width: 14, height: 30, offsetY: 4, color: GOLD, alpha: 0.85, depth: 97, glow: true,
      tween: { y: -16, scaleX: 0.4, alpha: 0, duration: 320, ease: 'Sine.easeOut' } },
    groundCloud({ width: 40, height: 10, color: GOLD, alpha: 0.4, offsetY: 18, durationMs: 380 }),
    wispRise({ count: 3, spread: 10, radius: 2, color: 0xfff2b0, travel: 22, durationMs: 360 }),
  ],
};

export const PALADIN_KIT_VFX: VFXEffectConfig[] = [
  HAMMER_TAP_IMPACT,
  DIVINE_STORM_VORTEX,
  DIVINE_STORM_STRIKE,
  WRATH_HAMMER_RAISE,
  WRATH_HAMMER_IMPACT,
  FLASH_OF_LIGHT_BURST,
  DIVINE_SHIELD_CAST,
  DIVINE_SHIELD_STATE,
  RIGHTEOUS_SHIELD_SLAM,
  RIGHTEOUS_SHIELD_STATE,
  CONSECRATION_BURN,
];
